import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, ListFilter, SlidersHorizontal, Download } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Reusable client-side table sorting. `accessors` maps a sortKey -> value getter.
// Pass a `storageKey` to persist the chosen column + direction across visits.
export function useTableSort(defaultKey = null, defaultDir = "asc", storageKey = null) {
  const init = () => {
    if (storageKey) {
      try {
        const s = JSON.parse(localStorage.getItem("sort:" + storageKey) || "null");
        if (s && s.key) return s;
      } catch { /* ignore */ }
    }
    return { key: defaultKey, dir: defaultDir };
  };
  const [sortKey, setSortKey] = useState(() => init().key);
  const [sortDir, setSortDir] = useState(() => init().dir);
  useEffect(() => {
    if (storageKey) localStorage.setItem("sort:" + storageKey, JSON.stringify({ key: sortKey, dir: sortDir }));
  }, [storageKey, sortKey, sortDir]);
  const toggle = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortRows = (rows, accessors = {}) => {
    if (!sortKey) return rows;
    const acc = accessors[sortKey] || ((r) => r[sortKey]);
    const norm = (v) => {
      if (v == null) return "";
      if (typeof v === "boolean") return v ? 1 : 0;
      if (typeof v === "number") return v;
      return String(v).toLowerCase();
    };
    return [...rows].sort((a, b) => {
      const va = norm(acc(a)), vb = norm(acc(b));
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  };
  return { sortKey, sortDir, toggle, sortRows, setSortKey, setSortDir };
}

// Funnel dropdown rendered inside a header cell. `filter` = { value, onChange, options:[{value,label}], allValue }
function HeaderFilter({ filter, tid }) {
  const active = filter.value !== (filter.allValue ?? "all");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`filter-${tid}`} onClick={(e) => e.stopPropagation()}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-sm transition-colors ${active ? "bg-primary text-white" : "text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700"}`}>
          <ListFilter size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[10rem]">
        <DropdownMenuRadioGroup value={filter.value} onValueChange={filter.onChange}>
          {filter.options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value} data-testid={`filter-${tid}-${o.value}`} className="text-xs">
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Clickable sortable <th> with an optional column filter funnel.
export function SortTh({ label, sortKey: k, sort, className = "", align = "left", filter = null }) {
  const active = sort.sortKey === k;
  return (
    <th className={`px-4 py-3 ${className}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <button
          type="button"
          onClick={() => sort.toggle(k)}
          data-testid={`sort-${k}`}
          className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider transition-colors hover:text-zinc-900 ${active ? "text-zinc-900" : "text-inherit"}`}
        >
          {label}
          {active ? (sort.sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronsUpDown size={12} className="opacity-30" />}
        </button>
        {filter && <HeaderFilter filter={filter} tid={k} />}
      </span>
    </th>
  );
}

// Persisted show/hide state for optional (secondary) table columns.
export function useColumns(storageKey, defaultHidden = []) {
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem("cols:" + storageKey);
      if (raw == null) return new Set(defaultHidden);
      return new Set(JSON.parse(raw));
    } catch { return new Set(defaultHidden); }
  });
  useEffect(() => {
    localStorage.setItem("cols:" + storageKey, JSON.stringify([...hidden]));
  }, [storageKey, hidden]);
  const show = (key) => !hidden.has(key);
  const toggle = (key) => setHidden((h) => { const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n; });
  return { show, toggle };
}

const toolBtnCls = "flex h-9 items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 text-xs font-semibold uppercase tracking-wide text-zinc-600 transition-colors hover:bg-zinc-50";

// Toolbar dropdown to toggle optional columns. `columns` = [{key,label}].
export function ColumnPicker({ columns, cols, label = "Columns" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="column-picker" className={toolBtnCls}>
          <SlidersHorizontal size={14} /> {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((c) => (
          <DropdownMenuCheckboxItem key={c.key} checked={cols.show(c.key)} onCheckedChange={() => cols.toggle(c.key)}
            data-testid={`column-toggle-${c.key}`} className="text-xs">
            {c.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// One-click CSV export button. `headers` = [{key,label}]; `rows` = array of plain objects.
export function ExportCsvButton({ filename, headers, rows, label = "Export CSV" }) {
  const onExport = () => {
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [headers.map((h) => esc(h.label)).join(",")];
    rows.forEach((r) => lines.push(headers.map((h) => esc(r[h.key])).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  return (
    <button type="button" onClick={onExport} data-testid="export-csv" className={toolBtnCls}>
      <Download size={14} /> {label}
    </button>
  );
}
